import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Hls from 'hls.js';
import GradedCanvas from '../components/GradedCanvas';
import CropBox from '../components/CropBox';
import ClipFeedbackPanel from '../components/ClipFeedbackPanel';
import EditTree from '../components/EditTree';
import { bust } from '../utils/cacheBust';
import {
  useAsset,
  useClipProofs,
  startClipSession,
  updateClipLayer,
  updateClipSummary,
  appendFeedback,
  setProofOverride,
  requeueProof,
  requeueAllProofs,
  addProofFrames,
  authorizeClipBake,
} from '../data/firestore';
import { getAssetPlaybackUrl } from '../data/uploadService';
import firebase from '../firebaseConfig';
import { httpsify } from '../utils/url';
import { composeProofGrade, resolveProofCrop } from '../utils/grade';
import { fmtTime } from '../utils/movie';

const API = process.env.REACT_APP_UPLOAD_API || '';
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// Human-readable perception line for the AI context (regions + WB reference).
function perceptionLine(base) {
  const p = base?.perception;
  if (!p) return '';
  const regions = p.regions || {};
  const cov = Object.keys(regions)
    .filter((k) => regions[k]?.coverage)
    .sort((a, b) => regions[b].coverage - regions[a].coverage)
    .map((k) => `${k} ${Math.round(regions[k].coverage * 100)}%`);
  return `white-balance ref: ${p.wbRef || 'gray-world'}; regions: ${cov.join(', ') || 'n/a'}`;
}

// Tiny client-side clip summary from the delta layer (until the worker writes one).
function quickClipSummary(clipLayer) {
  const ops = (clipLayer?.ops || []).filter((o) => o && o.delta);
  if (!ops.length && !clipLayer?.crop) return 'Per-frame auto-grade only — no clip feedback yet.';
  const bits = ops.map((o) => `${o.key} ${o.delta > 0 ? '+' : ''}${o.delta}`);
  if (clipLayer?.crop) bits.push('clip reframe');
  return `Clip-wide: ${bits.join(', ')}. Each frame keeps its own auto white-balance/exposure; these only nudge it.`;
}

// Capture a downscaled JPEG dataURL from a canvas/video element.
function captureEl(el, w0, h0, label) {
  const w = w0 || el?.videoWidth || el?.width;
  const h = h0 || el?.videoHeight || el?.height;
  if (!w || !h) return null;
  const s = Math.min(1, 1024 / w);
  const oc = document.createElement('canvas');
  oc.width = Math.max(1, Math.round(w * s));
  oc.height = Math.max(1, Math.round(h * s));
  const ctx = oc.getContext('2d');
  if (!ctx) return null;
  try {
    ctx.drawImage(el, 0, 0, oc.width, oc.height);
    return { label, dataUrl: oc.toDataURL('image/jpeg', 0.85) };
  } catch { return null; }
}

// Per-clip iterative grading session: review proofs, give clip-wide / per-proof
// natural-language feedback, watch the live preview, then authorize a fan-out
// bake of every frame in the clip.
export default function ClipGradeSession() {
  const { tripId, clipId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { doc: clip } = useAsset(tripId, clipId);
  const proofs = useClipProofs(tripId, clipId) || [];

  const session = clip?.gradeSession || null;
  const clipLayer = useMemo(() => session?.clipLayer || { ops: [], crop: null }, [session]);
  const status = session?.status || 'drafting';

  const [selectedId, setSelectedId] = useState(searchParams.get('proof') || null);
  const selected = proofs.find((p) => p.id === selectedId) || proofs[0] || null;

  const [reframe, setReframe] = useState(false);
  const [busyMsg, setBusyMsg] = useState('');
  const [err, setErr] = useState('');

  // Add-frames state.
  const [nlFrames, setNlFrames] = useState('');
  const [addBusy, setAddBusy] = useState(false);

  // Live preview proxy video.
  const videoRef = useRef(null);
  const [videoEl, setVideoEl] = useState(null);
  const [url, setUrl] = useState('');
  const [t, setT] = useState(0);
  const [dur, setDur] = useState(0);

  const isRed = !!clip?.isRawVideo;
  const fps = Number(clip?.srcFps || clip?.fpsAvg || clip?.metadata?.video?.fpsAvg || 0) || 24;

  // Seed the session shape once the clip resolves.
  useEffect(() => {
    if (clip && clipId) startClipSession(tripId, clipId, { fps }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clip?.id]);

  // Resolve the proxy playback URL (RED → CDN HLS; raw video → presigned MP4).
  useEffect(() => {
    let on = true;
    setUrl('');
    if (!clip) return undefined;
    if (isRed) {
      if (clip.proxy?.status === 'ready' && clip.proxyHlsUrl) setUrl(httpsify(clip.proxyHlsUrl));
    } else if (clip.proxyHlsUrl) {
      setUrl(httpsify(clip.proxyHlsUrl));
    } else if (clip.s3Key) {
      getAssetPlaybackUrl(clip.s3Key).then((u) => on && setUrl(u)).catch(() => {});
    }
    return () => { on = false; };
  }, [clip, isRed]);

  // Attach hls.js for the proxy stream; native src for MP4.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !url) return undefined;
    const isHls = isRed || url.includes('.m3u8');
    if (isHls && Hls.isSupported()) {
      const hls = new Hls({ enableWorker: true });
      hls.loadSource(url);
      hls.attachMedia(v);
      return () => hls.destroy();
    }
    v.src = url;
    return undefined;
  }, [url, isRed]);

  // Seek the proxy to the selected proof's moment for the live preview.
  const seekToSelected = useCallback(() => {
    const v = videoRef.current;
    if (!v || !selected) return;
    const sec = Number(selected.stillGrab?.timeSec);
    const fromIndex = Number(selected.stillGrab?.frameIndex) / fps;
    const target = Number.isFinite(sec) && sec > 0 ? sec : (Number.isFinite(fromIndex) ? fromIndex : 0);
    v.pause();
    v.currentTime = clamp(target, 0, dur || v.duration || target);
  }, [selected, fps, dur]);

  useEffect(() => { seekToSelected(); }, [selectedId, dur, seekToSelected]);

  // The composed grade + crop for the selected proof's live preview.
  const previewGrade = useMemo(
    () => (selected?.autoGradeBase ? composeProofGrade(selected.autoGradeBase, clipLayer, selected.override) : null),
    [selected, clipLayer]
  );
  const previewCrop = useMemo(
    () => resolveProofCrop(selected?.crop || null, clipLayer, selected?.override),
    [selected, clipLayer]
  );
  const getCrop = useCallback(() => previewCrop, [previewCrop]);

  // Capture master (proxy frame) + graded (preview canvas) for the AI.
  const captureFrames = useCallback(async () => {
    const frames = [];
    const v = videoRef.current;
    const m = captureEl(v, v?.videoWidth, v?.videoHeight, 'original master (proxy)');
    if (m) frames.push(m);
    const cv = document.querySelector('.cs-cap-clip');
    if (cv && cv.width) {
      const g = captureEl(cv, cv.width, cv.height, 'current graded proof');
      if (g) frames.push(g);
    }
    return frames;
  }, []);

  const perceptionText = useMemo(() => perceptionLine(selected?.autoGradeBase), [selected]);

  // ---- feedback handlers ----------------------------------------------------
  const onApplyClip = async (nextLayer, meta) => {
    setErr('');
    try {
      await updateClipLayer(tripId, clipId, nextLayer);
      await appendFeedback(tripId, clipId, { role: 'user', text: meta.text, scope: 'clip' });
      await appendFeedback(tripId, clipId, { role: 'assistant', text: meta.reply, scope: 'clip', ops: meta.ops });
      await updateClipSummary(tripId, clipId, { text: meta.summary || quickClipSummary(nextLayer), facts: null });
      await requeueAllProofs(tripId, proofs, nextLayer);
    } catch (e) { setErr(e.message || 'Could not apply feedback'); }
  };

  const onApplyProof = async (proof, nextOverride, meta) => {
    setErr('');
    try {
      await setProofOverride(tripId, proof.id, nextOverride);
      await appendFeedback(tripId, clipId, { role: 'user', text: meta.text, scope: `proof:${proof.id}` });
      await appendFeedback(tripId, clipId, { role: 'assistant', text: meta.reply, scope: `proof:${proof.id}`, ops: meta.ops });
      await requeueProof(tripId, { ...proof, override: nextOverride }, clipLayer);
    } catch (e) { setErr(e.message || 'Could not apply proof tweak'); }
  };

  // ---- reframe (per-proof crop) --------------------------------------------
  const cropTimer = useRef(null);
  const onCropChange = (rect) => {
    if (!selected) return;
    const ov = { ops: selected.override?.ops || [], crop: rect && rect.w > 0 ? rect : null };
    if (cropTimer.current) clearTimeout(cropTimer.current);
    cropTimer.current = setTimeout(() => {
      setProofOverride(tripId, selected.id, ov)
        .then(() => requeueProof(tripId, { ...selected, override: ov }, clipLayer))
        .catch((e) => setErr(e.message || 'Could not save reframe'));
    }, 700);
  };

  // ---- add frames -----------------------------------------------------------
  const addCurrentFrame = async () => {
    if (!clip) return;
    setAddBusy(true);
    setErr('');
    try {
      const frameIndex = Math.round((videoRef.current?.currentTime || t) * fps);
      await addProofFrames(tripId, clip, [frameIndex], clipLayer);
    } catch (e) { setErr(e.message || 'Could not add frame'); } finally { setAddBusy(false); }
  };

  const requestFramesNL = async () => {
    const message = nlFrames.trim();
    if (!message || !clip) return;
    setAddBusy(true);
    setErr('');
    try {
      const user = firebase.auth().currentUser;
      const token = user ? await user.getIdToken() : null;
      const res = await fetch(`${API}/ai/clip-frames`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          message,
          fps,
          lastFrame: Math.round((dur || 0) * fps),
          count: 5,
          existingFrames: proofs.map((p) => p.stillGrab?.frameIndex).filter((n) => Number.isFinite(n)),
        }),
      });
      if (!res.ok) throw new Error(`request failed (${res.status})`);
      const data = await res.json();
      const frames = Array.isArray(data.frames) ? data.frames : [];
      if (!frames.length) throw new Error('no frames suggested — try a more specific request');
      await addProofFrames(tripId, clip, frames, clipLayer);
      setNlFrames('');
    } catch (e) { setErr(e.message || 'Could not request frames'); } finally { setAddBusy(false); }
  };

  // ---- authorize ------------------------------------------------------------
  const authorize = async () => {
    if (!clip) return;
    setBusyMsg('Authorizing full-clip bake…');
    setErr('');
    try {
      const jobId = await authorizeClipBake(tripId, clip, clipLayer);
      setBusyMsg('');
      navigate(`/jobs?job=${jobId}`);
    } catch (e) { setErr(e.message || 'Could not authorize'); setBusyMsg(''); }
  };

  const clipSummary = session?.clipSummary?.text || quickClipSummary(clipLayer);
  const baking = status === 'authorized' || status === 'baking';
  // Honest state label (don't show the raw default 'drafting' when nothing's happening).
  const bakingProofCount = proofs.filter((p) => p.stillGrab?.status === 'queued' || p.stillGrab?.status === 'rendering').length;
  const stateLabel = baking
    ? 'baking full clip'
    : bakingProofCount
      ? `baking ${bakingProofCount} proof${bakingProofCount === 1 ? '' : 's'}`
      : status === 'done'
        ? 'baked'
        : session?.feedback?.length
          ? 'in review'
          : proofs.length
            ? 'proofs ready'
            : 'no proofs';

  // The live GradedCanvas runs over the low-bitrate proxy, so it posterizes/bands
  // — too rough to judge the grade. Each proof already has a full-res baked still
  // (from the real negative); show THAT as the preview, and only fall back to the
  // live canvas while reframing or while the proof is (re)baking.
  const selBaking = selected?.stillGrab?.status === 'queued' || selected?.stillGrab?.status === 'rendering';
  const bakedUrl = bust(selected?.derivatives?.magazine || selected?.displayUrl || '', selected);
  const showBaked = !!bakedUrl && !reframe && !selBaking;

  if (clip === null) return <div className="cs-section"><div className="cs-empty">Clip not found.</div></div>;
  if (!clip) return <div className="cs-section"><div className="cs-empty"><span className="cs-spinner" /> Loading clip…</div></div>;

  return (
    <div className="cs-section cs-clipgrade">
      <div className="cs-clipgrade-head">
        <div>
          <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => navigate(-1)}>← Back</button>
          <h2 style={{ margin: '8px 0 2px' }}>Grade session — {clip.name}</h2>
          <div className="cs-help" style={{ fontSize: 13 }}>
            {proofs.length} proof{proofs.length === 1 ? '' : 's'} · <b>{stateLabel}</b>
            {isRed ? ' · RED' : ''} · {fps.toFixed(0)} fps
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {baking && <span className="cs-grab-livetag">● baking full clip</span>}
          <button
            className="cs-btn cs-btn-primary"
            onClick={authorize}
            disabled={!!busyMsg || !proofs.length || baking}
            title="Bake every frame from this clip with the dialed-in look"
          >
            {busyMsg || (baking ? 'Bake authorized' : '✓ Authorize full clip bake')}
          </button>
        </div>
      </div>

      {err && <div className="cs-empty" style={{ borderColor: 'var(--danger, #c33)', marginBottom: 10 }}>{err}</div>}

      <div className="cs-clipgrade-body">
        <div>
          {/* Live preview of the selected proof */}
          <div className="cs-grab-stage" style={{ position: 'relative' }}>
            <video
              ref={(el) => { videoRef.current = el; setVideoEl(el); }}
              crossOrigin="anonymous"
              playsInline
              muted
              className="cs-grab-video"
              style={{ opacity: previewGrade ? 0 : 1 }}
              onLoadedMetadata={(e) => { setDur(e.target.duration || 0); seekToSelected(); }}
              onTimeUpdate={(e) => setT(e.target.currentTime || 0)}
            />
            {videoEl && previewGrade && (
              <GradedCanvas
                source={videoEl}
                grade={previewGrade}
                getCrop={reframe ? undefined : getCrop}
                className="cs-cap-clip cs-grab-graded"
              />
            )}
            {reframe && (
              <CropBox rect={previewCrop || { x: 0.1, y: 0.1, w: 0.8, h: 0.8 }} onChange={onCropChange} />
            )}
            {/* Full-res baked still on top — the real result, no proxy artifacts. */}
            {showBaked && (
              <img
                src={bakedUrl}
                alt="baked proof"
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', background: '#000' }}
              />
            )}
            <span className="cs-grab-livetag" style={{ position: 'absolute', top: 8, left: 8 }}>
              {showBaked ? '✓ baked · full res' : selBaking ? '● baking…' : reframe ? 'live preview · reframe' : 'live preview'}
            </span>
            {!previewGrade && !showBaked && (
              <div className="cs-empty" style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {selected ? 'Baking the base grade for this proof…' : 'No proofs yet — add frames below.'}
              </div>
            )}
          </div>

          <div className="cs-grab-row" style={{ marginTop: 8, gap: 10, alignItems: 'center' }}>
            <span className="cs-grab-frame"><strong>f{Math.round(t * fps)}</strong>{dur ? ` / ${Math.round(dur * fps)}` : ''}<span className="cs-grab-tc">{fmtTime(t)} / {fmtTime(dur)}</span></span>
            <span style={{ flex: 1 }} />
            <label className="cs-aiasst-alignchk" title="Reframe / zoom this proof — drag the box, it re-bakes the proof">
              <input type="checkbox" checked={reframe} onChange={(e) => setReframe(e.target.checked)} /> Reframe this proof
            </label>
          </div>
          <input
            className="cs-slider cs-grab-seek"
            type="range"
            min="0"
            max={dur || 0}
            step={1 / fps}
            value={Math.min(t, dur || 0)}
            onChange={(e) => { const v = videoRef.current; if (v) { v.currentTime = Number(e.target.value); } setT(Number(e.target.value)); }}
            style={{ width: '100%', marginTop: 6 }}
            aria-label="Scrub clip"
          />

          {/* Proof grid */}
          <div className="cs-clipgrade-proofs" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10, marginTop: 14 }}>
            {proofs.map((p) => {
              const sel = p.id === (selected?.id);
              const img = bust(p.derivatives?.magazine || p.displayUrl || p.thumbUrl, p);
              const baking2 = p.stillGrab?.status === 'queued' || p.stillGrab?.status === 'rendering';
              return (
                <button
                  key={p.id}
                  type="button"
                  className="cs-aiasst-tile"
                  onClick={() => setSelectedId(p.id)}
                  style={{ textAlign: 'left', border: sel ? '2px solid var(--surf)' : '2px solid transparent', borderRadius: 8, padding: 0, background: 'none', cursor: 'pointer' }}
                >
                  <div style={{ position: 'relative', aspectRatio: '16/9', background: '#111', borderRadius: 6, overflow: 'hidden' }}>
                    {img ? <img src={img} alt={`frame ${p.stillGrab?.frameIndex ?? ''}`} style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: baking2 ? 0.5 : 1 }} /> : <div className="cs-empty" style={{ height: '100%' }} />}
                    {baking2 && <span className="cs-grab-livetag" style={{ position: 'absolute', top: 6, left: 6 }}>● baking</span>}
                  </div>
                  <div style={{ padding: '6px 4px' }}>
                    <strong style={{ fontSize: 12 }}>f{p.stillGrab?.frameIndex ?? '?'}</strong>
                    {p.override?.ops?.length ? <span className="cs-aiasst-op cs-aiasst-op-preset" style={{ marginLeft: 6 }} title="Per-frame adjustments on top of the auto grade">Tuned</span> : null}
                    {p.gradeSummary?.text && <div className="cs-help" style={{ fontSize: 11, marginTop: 2, lineHeight: 1.3 }}>{p.gradeSummary.text}</div>}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Add frames */}
          <div className="cs-panel" style={{ marginTop: 14, padding: 12 }}>
            <strong style={{ fontSize: 13 }}>Add proof frames</strong>
            <div className="cs-grab-row" style={{ marginTop: 8, gap: 8 }}>
              <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={addCurrentFrame} disabled={addBusy || !url}>
                {addBusy ? '…' : `+ Add current frame (f${Math.round(t * fps)})`}
              </button>
            </div>
            <div className="cs-grab-row" style={{ marginTop: 8, gap: 8 }}>
              <input
                className="cs-input cs-input-sm"
                placeholder='e.g. "5 more across the barrel" or "the takeoff"'
                value={nlFrames}
                onChange={(e) => setNlFrames(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') requestFramesNL(); }}
                style={{ flex: 1 }}
                disabled={addBusy}
              />
              <button className="cs-btn cs-btn-primary cs-btn-sm" onClick={requestFramesNL} disabled={addBusy || !nlFrames.trim()}>
                ✨ Suggest &amp; add
              </button>
            </div>
          </div>
        </div>

        {/* Right column: feedback + summary */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="cs-panel" style={{ padding: 12 }}>
            <strong style={{ fontSize: 13 }}>Clip summary</strong>
            <p className="cs-help" style={{ fontSize: 13, marginTop: 6, lineHeight: 1.4 }}>{clipSummary}</p>
            {!!(clipLayer.ops || []).length && (
              <div className="cs-aiasst-ops">
                {clipLayer.ops.map((o, i) => <span key={i} className="cs-aiasst-op">{o.key} {o.delta > 0 ? '+' : ''}{o.delta}</span>)}
              </div>
            )}
          </div>

          {selected?.aiCritique && (
            <div className="cs-panel" style={{ padding: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <strong style={{ fontSize: 13 }}>AI auto-QC</strong>
                <span
                  className="cs-aiasst-op"
                  style={{ background: selected.aiCritique.score >= 4 ? 'rgba(54,211,153,.18)' : 'rgba(245,158,66,.18)' }}
                  title="The vision model's quality score for this proof (5 = gallery-ready)"
                >
                  {selected.aiCritique.score}/5
                </span>
                {!(selected.aiCritique.ops || []).length && (
                  <span className="cs-help" style={{ fontSize: 11 }}>· no change needed</span>
                )}
              </div>
              {selected.aiCritique.summary && (
                <p className="cs-help" style={{ fontSize: 13, marginTop: 6, lineHeight: 1.4 }}>{selected.aiCritique.summary}</p>
              )}
              {!!(selected.aiCritique.issues || []).length && (
                <div className="cs-help" style={{ fontSize: 12, marginTop: 2 }}>
                  Issues: {selected.aiCritique.issues.join(' · ')}
                </div>
              )}
              {!!(selected.aiCritique.ops || []).length && (
                <div className="cs-aiasst-ops" style={{ marginTop: 6 }}>
                  {selected.aiCritique.ops.map((o, i) => (
                    <span key={i} className="cs-aiasst-op">{o.key} {o.delta > 0 ? '+' : ''}{o.delta}</span>
                  ))}
                </div>
              )}
              <div className="cs-help" style={{ fontSize: 11, marginTop: 6, opacity: 0.7 }}>
                Auto-applied to this proof. Corrections shared across proofs carry into the full clip bake.
              </div>
            </div>
          )}

          {selected && (
            <EditTree
              key={selected.id}
              editTree={selected.editTree}
              manifest={selected.editManifest}
              editTreeStatus={selected.editTreeStatus}
              tripId={tripId}
              assetId={selected.id}
            />
          )}

          <ClipFeedbackPanel
            clip={clip}
            clipLayer={clipLayer}
            selectedProof={selected}
            captureFrames={captureFrames}
            perceptionText={perceptionText}
            feedback={session?.feedback || []}
            onApplyClip={onApplyClip}
            onApplyProof={onApplyProof}
            disabled={baking}
          />
        </div>
      </div>
    </div>
  );
}
